// THE LOOP BREAKER.
//
// Two places used to send the browser to the Cognito hosted login with nothing stopping them from
// doing it again the moment it came back: the app component (when checkAuth reports "not
// authenticated") and the HTTP interceptor (on any 401). When the underlying cause survives the
// round trip — the API rejecting a token it cannot validate, a user with no permissions, a clock
// skew, a failed silent renew — the result is an infinite bounce: land on /?code=…&state=…, redirect
// out again before the library can exchange and clean the URL, land again. The address bar keeps the
// code and state because nothing ever finishes processing them, which is exactly what a loop looks
// like from the outside.
//
// A redirect to fix an expired session is right; a SECOND redirect within seconds of the first never
// is — whatever the first one was for, it did not work. This gates every automatic login redirect on
// a timestamp in sessionStorage: one per window, at most one per cooldown. If a redirect is refused,
// the caller shows the error instead, which is the outcome the user can actually act on ("your
// account has no access") rather than a browser that flickers between two domains.

const KEY = 'epi.lastLoginRedirect';
const CooldownMs = 30_000;

/** Requests an automatic redirect to the hosted login. False = refused, show the error instead. */
export function requestLoginRedirect(reason: string): boolean {
    let last = 0;
    try {
        last = Number(sessionStorage.getItem(KEY) ?? 0);
    } catch {
        // Storage unavailable (private mode, blocked cookies): fall through and allow one attempt.
    }

    const now = Date.now();
    if (last > 0 && now - last < CooldownMs) {
        // The loop we are here to prevent. Log loudly: the FIRST redirect's reason is the real bug,
        // and without this line it is invisible behind the flicker.
        console.error(
            `[auth] Suppressed a repeat login redirect (${reason}). ` +
            `A redirect ${Math.round((now - last) / 1000)}s ago did not resolve the problem — ` +
            `check the failing request: an authenticated session that still gets 401 means the API ` +
            `rejected the token (issuer/audience mismatch, clock skew) or the account lacks access.`
        );
        return false;
    }

    try {
        sessionStorage.setItem(KEY, String(now));
    } catch {
        // Non-fatal: worst case the cooldown is not remembered across a reload.
    }
    return true;
}

/** Called once the session is genuinely established, so the next real expiry may redirect again. */
export function clearLoginRedirectGuard(): void {
    try {
        sessionStorage.removeItem(KEY);
    } catch {
        // ignored
    }
}
